/**
 * ys142857 全球玩家(表) — binary downline table with layer serials (view only).
 * Register from 子账户管理: left 2.1 bottom / right 2.2 then 2.1 bottom.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  binaryTreeAPI,
  type GlobalTableFooter,
  type GlobalTableLegTip,
  type GlobalTableRow,
} from '../api/binary-tree';
import { ApiError } from '../api/client';
import { formatBeijingTime } from '@uo/shared';
import { EmptyState } from '../components/ui/EmptyState';
import { LoadingSkeleton } from '../components/ui/LoadingSkeleton';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import {
  globalTableQueryKey,
  GLOBAL_TABLE_PAGE_SIZE,
  GLOBAL_TABLE_STALE_MS,
  GLOBAL_TABLE_VISIBLE_DEPTH,
  prefetchGlobalTableDrill,
} from '../hooks/prefetchGlobalTable';
import {
  GlobalPlayersHelpModal,
  readGlobalPlayersHelpDismissed,
} from '../components/global-players/GlobalPlayersHelpModal';
import {
  THEMED_SURFACE_COLORS as C,
  ThemedFixedTopBar,
  ThemedInput,
  ThemedPanel,
} from '../components/ui/ThemedSurface';

const PAGE_SIZE = GLOBAL_TABLE_PAGE_SIZE;
const STALE_MS = GLOBAL_TABLE_STALE_MS;

function fmtSum(raw: string): string {
  const n = Number(raw);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function LegStackCell({
  total,
  reinvest,
  totalLabel,
  reinvestLabel,
}: {
  total: string;
  reinvest: string;
  totalLabel: string;
  reinvestLabel: string;
}) {
  return (
    <td className="px-2 py-1.5 font-mono text-center align-middle">
      <div className="leading-tight" title={totalLabel}>
        {fmtSum(total)}
      </div>
      <div className="my-0.5 border-t" style={{ borderColor: C.line }} />
      <div className="leading-tight" title={reinvestLabel}>
        {fmtSum(reinvest)}
      </div>
    </td>
  );
}

function formatLegPath(tip: GlobalTableLegTip | null | undefined): string {
  if (!tip?.path_serials?.length) return '—';
  return tip.path_serials.join('/');
}

function LegSummaryCard({
  label,
  tipLabel,
  rootPlayerNumber,
  tip,
  onExpand,
  expandLabel,
}: {
  label: string;
  tipLabel: string;
  rootPlayerNumber: string | null | undefined;
  tip: GlobalTableLegTip | null | undefined;
  onExpand?: () => void;
  expandLabel?: string;
}) {
  if (!tip) return null;
  const playerId = tip.player_number ?? rootPlayerNumber ?? String(tip.user_id);
  // Always clickable when tip exists — jumps to absolute bottom-left of that leg.
  const clickable = !!onExpand;
  return (
    <ThemedPanel
      className={`flex-1 p-3 ${clickable ? 'cursor-pointer active:scale-[0.99]' : ''}`}
      {...(clickable
        ? {
            role: 'button' as const,
            tabIndex: 0,
            onClick: onExpand,
            onKeyDown: (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') onExpand();
            },
          }
        : {})}
    >
      <div className="text-xs font-semibold" style={{ color: C.gold }}>
        {label}
      </div>
      <div className="mt-1 text-[11px]" style={{ color: C.text }}>
        ID: {playerId}
      </div>
      <div className="mt-0.5 text-[10px]" style={{ color: C.muted }}>
        {tipLabel}({formatLegPath(tip)})
      </div>
      {clickable ? (
        <div className="mt-2 text-[10px] font-semibold" style={{ color: '#e85d5d' }}>
          {expandLabel} ↓
        </div>
      ) : null}
    </ThemedPanel>
  );
}

export function GlobalPlayers() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const rootFromUrl = searchParams.get('root') ?? '';
  const rootIdFromUrl = searchParams.get('root_id') ?? '';
  const [search, setSearch] = useState('');
  const [rootFilter, setRootFilter] = useState(rootFromUrl);
  const [rootUserId, setRootUserId] = useState<number | undefined>(() => {
    const n = Number(rootIdFromUrl);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  });
  const [page, setPage] = useState(1);
  const [helpOpen, setHelpOpen] = useState(false);
  const awayFromMine = !!(rootFilter || (rootUserId != null && rootUserId > 0));

  useEffect(() => {
    if (!readGlobalPlayersHelpDismissed()) setHelpOpen(true);
  }, []);

  useEffect(() => {
    setRootFilter(rootFromUrl);
    const n = Number(rootIdFromUrl);
    setRootUserId(Number.isFinite(n) && n > 0 ? n : undefined);
  }, [rootFromUrl, rootIdFromUrl]);

  useEffect(() => {
    setPage(1);
  }, [rootFilter, rootUserId]);

  const queryKey = globalTableQueryKey(rootFilter, rootUserId ?? null, page);

  const tableQuery = useQuery({
    queryKey,
    queryFn: () =>
      binaryTreeAPI.getGlobalTable({
        ...(rootUserId != null && rootUserId > 0
          ? { root_user_id: rootUserId }
          : rootFilter
            ? { root_player_number: rootFilter }
            : {}),
        page,
        limit: PAGE_SIZE,
        max_depth: GLOBAL_TABLE_VISIBLE_DEPTH,
      }),
    staleTime: STALE_MS,
    // Keep previous rows only when paginating the same root — never when drilling to another node.
    placeholderData: (previousData, previousQuery) => {
      if (!previousQuery) return undefined;
      const sameRoot = previousQuery.queryKey[3] === queryKey[3];
      const sameUserId = previousQuery.queryKey[4] === queryKey[4];
      return sameRoot && sameUserId ? previousData : undefined;
    },
    refetchOnWindowFocus: false,
  });

  /** Search-by-number only: persist resolved root_user_id once fresh data arrives. */
  useEffect(() => {
    if (tableQuery.isPlaceholderData || tableQuery.isFetching) return;
    if (rootUserId != null && rootUserId > 0) return;
    const resolvedRootId = tableQuery.data?.root_user_id;
    if (!resolvedRootId || resolvedRootId <= 0 || !rootFilter) return;
    setRootUserId(resolvedRootId);
    setSearchParams({ root: rootFilter, root_id: String(resolvedRootId) }, { replace: true });
  }, [
    tableQuery.data?.root_user_id,
    tableQuery.isPlaceholderData,
    tableQuery.isFetching,
    rootFilter,
    rootUserId,
    setSearchParams,
  ]);

  const rows = tableQuery.data?.rows ?? [];
  const footer: GlobalTableFooter | null | undefined = tableQuery.data?.footer;
  const total = tableQuery.data?.total ?? 0;
  const hasMore = tableQuery.data?.has_more ?? false;
  const hasDeeper = tableQuery.data?.has_deeper ?? false;
  const showTableSkeleton =
    tableQuery.isLoading || (tableQuery.isFetching && rows.length === 0);
  const isDrillFetching = tableQuery.isFetching && tableQuery.isPlaceholderData;
  const expandTipIds = new Set(
    [footer?.left_tip?.user_id, footer?.right_tip?.user_id].filter(
      (id): id is number => id != null && id > 0,
    ),
  );

  /** 上一层：回到当前根节点的双轨父节点（换根，重置页码）。 */
  const canGoPreviousLayer = !!footer?.parent_root;
  /** 向上翻页：同一根节点下的列表上一页（不换根）。 */
  const canGoPageUp = page > 1;
  const showNavButtons = canGoPreviousLayer || canGoPageUp;

  function applyRootFilter(playerNumber: string, userId?: number) {
    const next = playerNumber.trim();
    setRootFilter(next);
    setRootUserId(userId != null && userId > 0 ? userId : undefined);
    setSearch('');
    setPage(1);
    const params: Record<string, string> = {};
    if (next) params.root = next;
    if (userId != null && userId > 0) params.root_id = String(userId);
    setSearchParams(Object.keys(params).length > 0 ? params : {});
  }

  function runSearch() {
    const q = search.trim();
    if (/^\d+$/.test(q)) {
      applyRootFilter(q);
      return;
    }
    if (!q) {
      applyRootFilter('');
    }
  }

  function prefetchRow(row: GlobalTableRow) {
    const num = row.player_number ?? String(row.user_id);
    prefetchGlobalTableDrill(qc, num, row.user_id);
  }

  function onIdClick(row: GlobalTableRow) {
    const num = row.player_number ?? String(row.user_id);
    applyRootFilter(num, row.user_id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function drillToTip(tip: GlobalTableLegTip) {
    const num = tip.player_number?.trim() || String(tip.user_id);
    applyRootFilter(num, tip.user_id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function goPreviousLayer() {
    const parent = footer?.parent_root;
    if (!parent) return;
    const num = parent.player_number?.trim() || String(parent.user_id);
    applyRootFilter(num, parent.user_id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function goPageUp() {
    if (page <= 1) return;
    setPage((p) => Math.max(1, p - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function backToMine() {
    applyRootFilter('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  return (
    <div className="relative min-h-full" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <ThemedPageHeader
          title={t('globalPlayers.title', '全球玩家(表)')}
          onBack={() => navigate(-1)}
          theme={C}
          action={t('nearby.title', '附近玩家')}
          onAction={() => navigate('/nearby')}
        />
        <ThemedFixedTopBar background={C.topbarBg}>
          <ThemedPanel className="p-4">
            <div className="flex gap-2">
              <ThemedInput
                type="text"
                placeholder={t('myFriends.searchPlaceholder', '搜索好友玩家ID')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && runSearch()}
              />
              <button
                type="button"
                onClick={() => setHelpOpen(true)}
                className="shrink-0 rounded-lg border px-3 text-sm font-semibold"
                style={{ borderColor: `${C.gold}66`, color: C.gold, background: C.chipBg }}
                aria-label={t('globalPlayers.helpTitle', '粉丝列表对应图')}
              >
                ?
              </button>
              <button
                type="button"
                onClick={runSearch}
                className="shrink-0 rounded-lg border px-4 text-sm font-semibold"
                style={{ borderColor: `${C.gold}66`, color: C.gold, background: C.chipBg }}
              >
                {t('common.search', '搜索')}
              </button>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs" style={{ color: C.muted }}>
              {rootFilter ? (
                <span>
                  {t('globalPlayers.filteredBy', '筛选根节点 ID')}: {rootFilter}
                </span>
              ) : null}
              <button
                type="button"
                onClick={() => navigate('/wallet')}
                className="rounded-md border px-2.5 py-1 text-[11px] font-semibold"
                style={{ borderColor: `${C.gold}88`, color: C.gold, background: `${C.gold}14` }}
              >
                {t('globalPlayers.backHome', '返回主页')}
              </button>
              <button
                type="button"
                onClick={backToMine}
                disabled={!awayFromMine}
                className="rounded-md border px-2.5 py-1 text-[11px] font-semibold disabled:opacity-40"
                style={{ borderColor: `${C.gold}88`, color: C.gold, background: `${C.gold}14` }}
              >
                {t('globalPlayers.backToMine', '返回我的')}
              </button>
              {rootFilter ? (
                <button
                  type="button"
                  onClick={() => applyRootFilter('')}
                  className="underline"
                  style={{ color: C.gold }}
                >
                  {t('globalPlayers.clearFilter', '清除')}
                </button>
              ) : null}
            </div>
          </ThemedPanel>
        </ThemedFixedTopBar>

        <div className="overflow-y-auto px-3 pt-[196px] pb-8">
          {isDrillFetching ? (
            <div
              className="pointer-events-none fixed inset-x-0 top-[196px] z-10 mx-auto h-0.5 max-w-[430px] overflow-hidden"
              aria-hidden
            >
              <div
                className="h-full w-1/3 animate-pulse"
                style={{ background: C.gold, opacity: 0.7 }}
              />
            </div>
          ) : null}
          {showTableSkeleton ? (
            <div className="flex flex-col gap-2 px-2">
              <div className="px-1 py-2 text-center text-xs" style={{ color: C.muted }}>
                {t('globalPlayers.loadingDownline', '加载中…')}
              </div>
              <LoadingSkeleton className="h-10" />
              <LoadingSkeleton className="h-10" />
              <LoadingSkeleton className="h-10" />
              <LoadingSkeleton className="h-10" />
            </div>
          ) : rows.length === 0 ? (
            tableQuery.isError ? (
              <EmptyState
                title={
                  tableQuery.error instanceof ApiError
                    ? tableQuery.error.message
                    : t('common.loadFailed', '加载失败')
                }
              />
            ) : (
              <EmptyState title={t('common.noData', '暂无数据')} />
            )
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between px-1 text-xs" style={{ color: C.muted }}>
                <span>
                  {t('globalPlayers.layerHint', '显示 {{depth}} 层', { depth: GLOBAL_TABLE_VISIBLE_DEPTH })}
                  {footer?.left_tip || footer?.right_tip
                    ? ` · ${t('globalPlayers.tapToExpand', '点击最左下一键到底')}`
                    : hasDeeper
                      ? ` · ${t('globalPlayers.tapToExpand', '点击最左下一键到底')}`
                      : ''}
                </span>
                <span>
                  {t('globalPlayers.totalRows', '共 {{count}} 条', { count: total })}
                </span>
              </div>
              <div className="overflow-x-auto rounded-xl border" style={{ borderColor: C.line }}>
                <table className="w-full min-w-[370px] text-left text-[11px]">
                  <thead>
                    <tr style={{ color: C.muted, background: C.inputBg }}>
                      <th className="px-2 py-2 font-medium text-center">
                        {t('globalPlayers.colSerial', '序号')}
                      </th>
                      <th className="px-2 py-2 font-medium">ID</th>
                      <th className="px-2 py-2 font-medium">{t('myFriends.colNickname', '昵称')}</th>
                      <th className="px-2 py-2 font-medium text-center">
                        <div>L(总)</div>
                        <div className="my-0.5 border-t" style={{ borderColor: C.line }} />
                        <div>L(复)</div>
                      </th>
                      <th className="px-2 py-2 font-medium text-center">
                        <div>R(总)</div>
                        <div className="my-0.5 border-t" style={{ borderColor: C.line }} />
                        <div>R(复)</div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.filter((row) => !row.is_register_slot).map((row) => {
                      const isExpandNode = expandTipIds.has(row.user_id) && row.has_children;
                      return (
                      <tr
                        key={`${row.serial}-${row.user_id}`}
                        className="border-t"
                        style={{
                          borderColor: C.line,
                          color: C.text,
                          background: isExpandNode ? 'rgba(232,93,93,0.08)' : undefined,
                        }}
                      >
                        <td className="px-2 py-2 text-center font-mono">
                          {row.serial}
                          {isExpandNode ? (
                            <div className="text-[9px]" style={{ color: '#e85d5d' }}>↓</div>
                          ) : null}
                        </td>
                        <td className="px-2 py-2">
                          <button
                            type="button"
                            onPointerEnter={() => prefetchRow(row)}
                            onClick={() => onIdClick(row)}
                            className="inline-flex items-center gap-0.5 font-mono underline decoration-dotted underline-offset-2"
                            style={{ color: C.gold }}
                          >
                            <span className="text-[10px]">{'★'.repeat(row.star_count)}</span>
                            {row.player_number ?? row.user_id}
                          </button>
                        </td>
                        <td className="px-2 py-2">
                          <div>{row.nickname ?? row.username}</div>
                          <div className="text-[9px] leading-tight" style={{ color: C.muted }}>
                            {formatBeijingTime(row.registered_at)}
                          </div>
                        </td>
                        <LegStackCell
                          total={row.left_total}
                          reinvest={row.left_reinvest}
                          totalLabel="L(总)"
                          reinvestLabel="L(复)"
                        />
                        <LegStackCell
                          total={row.right_total}
                          reinvest={row.right_reinvest}
                          totalLabel="R(总)"
                          reinvestLabel="R(复)"
                        />
                      </tr>
                    );
                    })}
                  </tbody>
                </table>
              </div>

              {footer && (footer.left_tip || footer.right_tip) ? (
                <div className="flex gap-2">
                  <LegSummaryCard
                    label={t('globalPlayers.legLeft', '左')}
                    tipLabel={t('globalPlayers.legBottomLeft', '最左下')}
                    rootPlayerNumber={footer.root_player_number}
                    tip={footer.left_tip}
                    expandLabel={t('globalPlayers.expandDown', '一键到底')}
                    onExpand={
                      footer.left_tip ? () => drillToTip(footer.left_tip!) : undefined
                    }
                  />
                  <LegSummaryCard
                    label={t('globalPlayers.legRight', '右')}
                    tipLabel={t('globalPlayers.legBottomRight', '最左下')}
                    rootPlayerNumber={footer.root_player_number}
                    tip={footer.right_tip}
                    expandLabel={t('globalPlayers.expandDown', '一键到底')}
                    onExpand={
                      footer.right_tip ? () => drillToTip(footer.right_tip!) : undefined
                    }
                  />
                </div>
              ) : null}

              {showNavButtons ? (
                <div className="flex gap-2 pb-2">
                  {canGoPageUp ? (
                    <button
                      type="button"
                      onClick={goPageUp}
                      disabled={tableQuery.isFetching}
                      className="flex-1 rounded-lg py-3 text-sm font-bold disabled:opacity-40"
                      style={{ background: C.gold, color: '#1a1208' }}
                    >
                      {t('globalPlayers.pageUp', '向上翻页')}
                    </button>
                  ) : null}
                  {canGoPreviousLayer ? (
                    <button
                      type="button"
                      onClick={goPreviousLayer}
                      disabled={tableQuery.isFetching}
                      className="flex-1 rounded-lg py-3 text-sm font-bold disabled:opacity-40"
                      style={{ background: C.gold, color: '#1a1208' }}
                    >
                      {t('globalPlayers.previousLayer', '上一层')}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
      <GlobalPlayersHelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  );
}
